import TextSkeleton from '@/components/Studies/Show/OverviewV2/TextSkeleton';
import Button from '@/components/UI/Button/Button';
import styles from '@/components/Account/BenchlingAuth/ConnectedAccount.module.scss';
import {
  type BenchlingLinkedUser,
  type BenchlingLinkStatus,
  disconnectBenchlingAuth,
  fetchBenchlingAuthStatus,
  fetchBenchlingLinkedUser,
  startBenchlingAuth,
  tenantHost,
} from '@/components/Account/BenchlingAuth/ConnectedAccount.utils';
import { errorToast, successToast } from '@/helpers';
import { _isNotBlank, _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { BenchlingLinkService, linkStateOf } from '@/support/benchlingLink/BenchlingLink.utils';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { type FC, useCallback, useEffect, useState } from 'react';
import { RiCheckLine, RiCloseLine } from 'react-icons/ri';
import { useSearchParams } from 'react-router-dom';

const statusChips: Record<
  Exclude<BenchlingLinkStatus, 'NOT_LINKED'>,
  { label: string; className: string; Icon: typeof RiCheckLine }
> = {
  LINKED: { label: 'Linked', className: styles.linked, Icon: RiCheckLine },
  LINK_EXPIRED: { label: 'Re-authentication required', className: styles.expired, Icon: RiCloseLine },
};

interface LinkedUserDetailsProps {
  /** Undefined while loading; null when the details could not be loaded. */
  linkedUser: BenchlingLinkedUser | null | undefined;
}

/**
 * Which Benchling account the link belongs to, so the user can confirm it is theirs.
 *
 * Shown for an expired link too: the server answers with the details stored when the link last
 * worked. Only the name is optional, as Benchling allows a user without one.
 */
const LinkedUserDetails: FC<LinkedUserDetailsProps> = ({ linkedUser }) => {
  if (linkedUser === undefined) {
    return <TextSkeleton width="14rem" lines={3} testId="connected-account__linked-user-loading" />;
  }
  if (linkedUser === null) {
    return <p className="ma0 f5 mid-gray">Benchling account details unavailable.</p>;
  }

  return (
    <div className="lh-copy f5">
      {_isNotBlank(linkedUser.name) && <span className="db basier-med near-black">{linkedUser.name}</span>}
      <span className="db">{linkedUser.username}</span>
      <span className="db mid-gray">{linkedUser.email}</span>
    </div>
  );
};

/**
 * Reports what the OAuth callback came back with, as a toast.
 *
 * Only reasons a user can act on are worded specifically. Everything else falls through to the
 * generic message with the reason appended, which is enough to correlate with a server log without
 * putting our internals in front of them.
 */
const toastCallbackOutcome = (result: string, reason: string | null): void => {
  if (result === 'success') {
    successToast('Benchling account linked');
    return;
  }

  // The link was refused because Benchling did not say whose account it is; nothing was saved.
  if (reason === 'identity_unavailable') {
    errorToast('Could not confirm which Benchling account you linked. Please try again.');
    return;
  }

  if (reason === 'unbound_user_agent') {
    errorToast('That link attempt did not come from this browser, so it was rejected.');
    return;
  }

  // Our own misconfiguration, not a Benchling rejection — nothing was sent. Worded so the user does
  // not retry, because retrying cannot help until the server is configured.
  if (reason === 'client_not_configured') {
    errorToast('Benchling account linking is not configured on this environment. Contact support.');
    return;
  }

  errorToast(`Could not link your Benchling account${_isNotBlank(reason) ? ` (${reason})` : ''}`);
};

/**
 * Links the acting user's In Vivo account to their Benchling Core account, so delegated actions are
 * attributed to them rather than to the integration's service account.
 *
 * <p>Sits as its own card beneath the profile/password tabs on the Account screen.
 */
const ConnectedAccount: FC = () => {
  const [status, setStatus] = useState<BenchlingLinkStatus | null>(null);
  const [tenantUrl, setTenantUrl] = useState<string | null>(null);
  const [linkedUser, setLinkedUser] = useState<BenchlingLinkedUser | null | undefined>(undefined);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const busy = linking || unlinking;
  const [searchParams, setSearchParams] = useSearchParams();

  const reportStatusFailure = useCallback((error: unknown): void => {
    ExceptionHandler.captureException(
      new InVivoError('Failed to load Benchling link status', {
        cause: error,
        slug: 'benchling-auth-status',
      })
    );
    errorToast('Could not load your Benchling link status');
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchBenchlingAuthStatus()
      .then(({ status: next, tenant_url: nextTenantUrl }) => {
        if (!cancelled) {
          setStatus(next);
          setTenantUrl(nextTenantUrl ?? null);
          // This screen reads the link directly, so it is the authority while open; delegated actions
          // elsewhere follow it. The expiry is kept when the status agrees, since this read has none.
          const known = BenchlingLinkService.get();
          BenchlingLinkService.set(linkStateOf(next, next === known.status ? known.refreshTokenExpiresAt : null));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          reportStatusFailure(error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [reportStatusFailure]);

  useEffect(() => {
    const result = searchParams.get('benchling_auth');
    if (!_notNil(result)) {
      return;
    }

    toastCallbackOutcome(result, searchParams.get('reason'));

    // Clear the parameters so a refresh does not re-toast a stale outcome.
    const next = new URLSearchParams(searchParams);
    next.delete('benchling_auth');
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const link = async (): Promise<void> => {
    setLinking(true);
    try {
      // A full navigation, not a popup: Benchling drives login, SSO and consent on its own origin and
      // redirects the browser back to our callback.
      window.location.assign(await startBenchlingAuth());
    } catch (error) {
      setLinking(false);
      ExceptionHandler.captureException(
        new InVivoError('Failed to start Benchling delegated auth', {
          cause: error,
          slug: 'benchling-auth-start',
        })
      );
      errorToast('Could not start linking your Benchling account');
    }
  };

  const unlink = async (): Promise<void> => {
    setUnlinking(true);
    try {
      await disconnectBenchlingAuth();
      setLinkedUser(null);
      setStatus('NOT_LINKED');
      BenchlingLinkService.set(linkStateOf('NOT_LINKED'));
      successToast('Benchling account unlinked. In Vivo can no longer act on your behalf.');
    } catch (error) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to unlink Benchling account', {
          cause: error,
          slug: 'benchling-auth-disconnect',
        })
      );
      errorToast('Could not unlink your Benchling account');
    } finally {
      setUnlinking(false);
    }
  };

  const hasLink = status === 'LINKED' || status === 'LINK_EXPIRED';
  // Only once the status has loaded, so the button is not disabled while it loads or if that read fails.
  const tenantMissing = _notNil(status) && !_isNotBlank(tenantUrl);

  useEffect(() => {
    if (!hasLink) {
      return;
    }

    let cancelled = false;
    fetchBenchlingLinkedUser()
      .then((user) => {
        if (!cancelled) {
          setLinkedUser(user);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLinkedUser(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [hasLink]);

  const chip = status === 'LINKED' || status === 'LINK_EXPIRED' ? statusChips[status] : null;

  return (
    <div className="mv3 ui-card mw7" data-testid="connected-account">
      <div className="pa4">
        <h3 className="f4 mt0 mb2">Connected account</h3>
        <p className="mb3 mid-gray">Link your account to your Benchling core account to enable delegated actions.</p>
        {_isNotBlank(tenantUrl) && (
          <p className="mb3 f5" data-testid="connected-account__tenant">
            <span className="basier-med near-black">Benchling tenant: </span>
            <span className="mid-gray">{tenantHost(tenantUrl)}</span>
          </p>
        )}
        {_notNil(chip) && (
          <div
            className="flex justify-between items-start ba b--moon-gray br2 pa3 mb3"
            data-testid="connected-account__details"
          >
            <div data-testid="connected-account__linked-user">
              <LinkedUserDetails linkedUser={linkedUser} />
            </div>
            <span
              className={`ml3 flex-shrink-0 ${styles.status} ${chip.className}`}
              data-testid="connected-account__status"
            >
              <span className={styles.statusIcon} aria-hidden="true">
                {/* Remix glyphs are filled outlines with no weight variant; a matching stroke thickens them evenly. */}
                <chip.Icon size={14} stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" />
              </span>
              {chip.label}
            </span>
          </div>
        )}
        <div className="flex items-center">
          <Button
            plain
            className={styles.darkLoader}
            disabled={busy || tenantMissing}
            loading={linking}
            tooltip={
              tenantMissing
                ? 'No Benchling tenant is configured for your team, so there is nothing to link to. Contact your administrator.'
                : undefined
            }
            onClick={link}
            testId="connected-account__link"
          >
            {hasLink ? 'Refresh' : 'Link account'}
          </Button>
          {hasLink && (
            <Button
              plain
              className={`ml2 ${styles.darkLoader}`}
              disabled={busy}
              loading={unlinking}
              onClick={unlink}
              testId="connected-account__unlink"
            >
              Unlink account
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectedAccount;
